import type { PublicKey } from "@solana/web3.js";
import type { ReserveData } from "@solendprotocol/solend-sdk/dist/classes/market";
import type {
  IsolatedVaultPositionMap,
  LendingCollateralPositionData,
  LendingCollateralPositionMap,
  LeveragedYieldPositionMap,
  LPYieldPositionData,
  NFTCollateralPositionMap,
} from "@stepfinance/lending";
import type BN from "bn.js";

import type { YieldDataMap } from "../../components/dashboard/Balances/YieldBearingAssets";
import type { CamelCase } from "..";

export type ReformattedCollateralizedPosition = {
  key: string;
  platform: string;
  asset: string;
  tokenSymbol: string;
  tokenPrice: number;
  deposited: number;
  depositApy: number | undefined;
  borrowed: number;
  borrowApy: number | undefined;
  net: number;
  netApy: number | undefined;
};

export type ReformattedYieldPosition = {
  key: string;
  platform: string;
  vault: [string, string];
  tokenSymbol: [string, string];
  equityUSD: number;
  debtTokenAmountA: number;
  debtTokenAmountB: number;
  debtTotalValueUSD: number;
  positionAmountA: number;
  positionAmountB: number;
  positionValueUSD: number;
  killBuffer: number;
};

export type ReformattedLPCollateralizedPosition = ReformattedCollateralizedPosition & {
  asset: [string, string];
  tokenSymbol: [string, string];
  lpDescription: "LP Tokens";
};

export type IsolatedPoolPositionMap = {
  [poolName: string]: LendingCollateralPositionMap;
};

export type LendingPlatforms =
  | "solend"
  | "tulip"
  | "port"
  | "larix"
  | "jet"
  | "apricot"
  | "francium"
  | "hubble"
  | "ratio"
  | "hedge"
  | "sharky"
  | "marginfi";

type BaseLendingData = {
  [Platform in LendingPlatforms as `${CamelCase<Platform>}`]: {
    collateral: LendingCollateralPositionMap;
    yieldFarms: LeveragedYieldPositionMap;
  };
};
type SolendLendingData = BaseLendingData & {
  solend: BaseLendingData["solend"] & {
    isolatedPools: IsolatedPoolPositionMap;
    mainReserveStats: (ReserveData & { collateralMintAddress: string })[];
    yieldDataMap: YieldDataMap;
    solendCTokenMints: string[];
  };
  larix: BaseLendingData["larix"] & {
    isolatedPools: IsolatedPoolPositionMap;
  };
};

export type IsolatedVaultLendingData = BaseLendingData & {
  ratio: BaseLendingData["ratio"] & {
    isolatedVaults: IsolatedVaultPositionMap;
  };
  hedge: BaseLendingData["hedge"] & {
    isolatedVaults: IsolatedVaultPositionMap;
  };
};

export type NFTLendingData = BaseLendingData & {
  sharky: BaseLendingData["sharky"] & {
    nftLending: NFTCollateralPositionMap;
  };
};

export type LendingData = BaseLendingData & SolendLendingData & IsolatedVaultLendingData & NFTLendingData;

export interface LastUpdate {
  slot: BN;
  stale: boolean;
}

export interface Reserve {
  version: number;
  lastUpdate: LastUpdate;
  lendingMarket: PublicKey;
  liquidity: ReserveLiquidity;
  collateral: ReserveCollateral;
  config: ReserveConfig;
}

export interface ReserveLiquidity {
  mintPubkey: PublicKey;
  mintDecimals: number;
  supplyPubkey: PublicKey;
  // @FIXME: oracle option
  oracleOption: number;
  pythOraclePubkey: PublicKey;
  switchboardOraclePubkey: PublicKey;
  availableAmount: BN;
  borrowedAmountWads: BN;
  cumulativeBorrowRateWads: BN;
  marketPrice: BN;
}

export interface ReserveCollateral {
  mintPubkey: PublicKey;
  mintTotalSupply: BN;
  supplyPubkey: PublicKey;
}

export interface ReserveConfig {
  optimalUtilizationRate: number;
  loanToValueRatio: number;
  liquidationBonus: number;
  liquidationThreshold: number;
  minBorrowRate: number;
  optimalBorrowRate: number;
  maxBorrowRate: number;
  fees: {
    borrowFeeWad: BN;
    hostFeePercentage: number;
  };
  depositLimit: BN;
}

export const isLPYieldPositionData = (
  position: LendingCollateralPositionData | LPYieldPositionData,
): position is LPYieldPositionData => {
  return (
    (position as LPYieldPositionData).mintA !== undefined &&
    (position as LPYieldPositionData).mintB !== undefined &&
    (position as LPYieldPositionData).tokenPrice !== undefined
  );
};

export const isSolendPlatformLendingData = (
  platformLendingData: LendingData[LendingPlatforms],
): platformLendingData is LendingData["solend"] | LendingData["larix"] =>
  Object.prototype.hasOwnProperty.call(platformLendingData, "isolatedPools");

export const isIsolatedVaultLendingData = (
  platformLendingData: LendingData[LendingPlatforms],
): platformLendingData is LendingData["ratio"] | LendingData["hedge"] => {
  return Object.prototype.hasOwnProperty.call(platformLendingData, "isolatedVaults");
};

export const isNFTLendingData = (
  platformLendingData: LendingData[LendingPlatforms],
): platformLendingData is LendingData["sharky"] => {
  return Object.prototype.hasOwnProperty.call(platformLendingData, "nftLending");
};
